import type { MsrObservationEntry, MsrObservationItem } from './msrReportTypes';

export interface MsrTextSegment {
  text: string;
  bold: boolean;
}

export function normalizeMsrObservation(entry: MsrObservationEntry): MsrObservationItem {
  if (typeof entry === 'string') return { text: entry };
  return { text: entry.text, children: entry.children ?? [] };
}

/**
 * Split observation prose into normal vs bold spans.
 * Bold: numeric counts and quoted alert / activity names (matches MSR slide template).
 */
export function parseMsrObservationSegments(text: string): MsrTextSegment[] {
  const segments: MsrTextSegment[] = [];
  let i = 0;

  while (i < text.length) {
    const ch = text[i]!;

    if (ch === "'" || ch === '"') {
      const quote = ch;
      let j = i + 1;
      while (j < text.length && text[j] !== quote) j += 1;
      const end = j < text.length ? j + 1 : j;
      segments.push({ text: text.slice(i, end), bold: true });
      i = end;
      continue;
    }

    if (ch === '$' && i + 1 < text.length) {
      const next = text[i + 1]!;
      if (next >= '0' && next <= '9') {
        let j = i + 1;
        while (j < text.length) {
          const c = text[j]!;
          if ((c >= '0' && c <= '9') || c === ',') j += 1;
          else break;
        }
        segments.push({ text: text.slice(i, j), bold: true });
        i = j;
        continue;
      }
    }

    if (ch >= '0' && ch <= '9') {
      let j = i;
      while (j < text.length) {
        const c = text[j]!;
        if ((c >= '0' && c <= '9') || c === ',') j += 1;
        else break;
      }
      segments.push({ text: text.slice(i, j), bold: true });
      i = j;
      continue;
    }

    let j = i + 1;
    while (j < text.length) {
      const c = text[j]!;
      if (c === "'" || c === '"' || (c >= '0' && c <= '9')) break;
      j += 1;
    }
    if (j > i) segments.push({ text: text.slice(i, j), bold: false });
    i = j;
  }

  return segments.filter((s) => s.text.length > 0);
}

export interface MsrWordToken {
  text: string;
  bold: boolean;
}

/** Split segments into words (and spaces) for line wrapping. */
export function msrObservationWordTokens(segments: MsrTextSegment[]): MsrWordToken[] {
  const tokens: MsrWordToken[] = [];

  for (const seg of segments) {
    const raw = seg.text;
    let start = 0;
    for (let i = 0; i <= raw.length; i++) {
      const isWs = i < raw.length && (raw[i] === ' ' || raw[i] === '\t');
      const atEnd = i === raw.length;
      if (atEnd || isWs) {
        if (i > start) {
          tokens.push({ text: raw.slice(start, i), bold: seg.bold });
        }
        if (!atEnd) tokens.push({ text: ' ', bold: seg.bold });
        start = i + 1;
      }
    }
  }

  return tokens;
}
